import { useParams, Link } from 'react-router-dom';
import {
  Check,
  X,
  Minus,
  Star,
  ArrowLeft,
  Mail,
  Map,
  DollarSign,
  Shield,
  Zap,
  BarChart3,
  Users,
  Phone,
  Globe,
  Target,
  Clock,
  XCircle,
  TrendingDown,
  Home,
  Eye,
  Brain,
  RotateCcw,
  CloudLightning,
  LucideIcon,
} from 'lucide-react';

import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { comparisonPageSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';

// Static imports — Vite requires explicit paths (no dynamic import with variables)
import jobnimbusData from '../data/comparisons/jobnimbus.json';
import acculynxData from '../data/comparisons/acculynx.json';
import leapData from '../data/comparisons/leap.json';
import roofrData from '../data/comparisons/roofr.json';
import zuperData from '../data/comparisons/zuper.json';
import rooferbaseData from '../data/comparisons/rooferbase.json';
import buildertrendData from '../data/comparisons/buildertrend.json';

// Type definitions matching actual JSON structure
interface WhyWeWinItem {
  title: string;
  description: string;
  icon: string;
}

interface FeatureRow {
  feature: string;
  rooflyzer: string;
  competitor: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface ComparisonData {
  title: string;
  slug: string;
  competitorName: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  heroHeadline: string;
  heroSubheadline: string;
  theirPricing: string;
  ourPricing: string;
  whyWeWin: WhyWeWinItem[];
  featureComparison: FeatureRow[];
  faqs: FAQ[];
}

// Map icon name strings from JSON to actual lucide-react components
const iconMap: Record<string, LucideIcon> = {
  Mail,
  Map,
  DollarSign,
  Shield,
  Zap,
  BarChart3,
  Users,
  Phone,
  Globe,
  Star,
  Target,
  Clock,
  Check,
  XCircle,
  TrendingDown,
  Home,
  Eye,
  Brain,
  RotateCcw,
  CloudLightning,
};

// Static lookup map — all JSON files bundled at build time
const comparisonMap: Record<string, ComparisonData> = {
  jobnimbus: jobnimbusData as ComparisonData,
  acculynx: acculynxData as ComparisonData,
  leap: leapData as ComparisonData,
  roofr: roofrData as ComparisonData,
  zuper: zuperData as ComparisonData,
  rooferbase: rooferbaseData as ComparisonData,
  buildertrend: buildertrendData as ComparisonData,
  // Alternative-URL aliases
  'jobnimbus-alternative': jobnimbusData as ComparisonData,
  'buildertrend-alternative': buildertrendData as ComparisonData,
};

function StatusBadge({ value }: { value: string }) {
  switch (value) {
    case 'yes':
      return <Check className="size-5 text-emerald-400 mx-auto" />;
    case 'no':
      return <X className="size-5 text-red-400/60 mx-auto" />;
    case 'partial':
      return <Minus className="size-5 text-yellow-400/60 mx-auto" />;
    case 'premium':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-300 mx-auto">
          <Star className="size-3.5" /> Premium
        </span>
      );
    default:
      return <Minus className="size-5 text-white/20 mx-auto" />;
  }
}

export const CompareDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const data: ComparisonData | undefined = slug ? comparisonMap[slug] : undefined;

  if (!data) {
    return (
      <div className="pt-32 pb-20 px-4 text-center">
        <h1 className="text-3xl font-bold text-white mb-4">Comparison Not Found</h1>
        <p className="text-white/70 mb-8">We couldn't find the comparison you're looking for.</p>
        <Link
          to="/compare"
          className="inline-flex items-center gap-2 text-slate-300 hover:text-white hover:underline"
        >
          <ArrowLeft className="size-4" />
          View All Comparisons
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEOHead
        title={data.metaTitle}
        description={data.metaDescription}
        canonicalUrl={`/compare/${data.slug}`}
        keywords={data.keywords}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(
            comparisonPageSchema({
              competitorName: data.competitorName,
              headline: data.metaTitle,
              description: data.metaDescription,
              url: `/compare/${data.slug}`,
            })
          )}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqPageSchema(data.faqs))}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/[0.02] pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Breadcrumbs
            items={[
              { name: 'Compare', url: '/compare' },
              { name: `vs ${data.competitorName}`, url: `/compare/${slug}` },
            ]}
            className="justify-center mb-8"
          />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{data.title}</h1>
          <p className="text-xl text-slate-200 font-medium mb-4">{data.heroHeadline}</p>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">{data.heroSubheadline}</p>

          {/* Pricing comparison */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-white/5 border border-white/10 rounded-lg px-6 py-4">
              <p className="text-white/60 text-sm mb-1">{data.competitorName}</p>
              <p className="text-white font-semibold">{data.theirPricing}</p>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-lg px-6 py-4">
              <p className="text-slate-400 text-sm mb-1">Rooflyzer</p>
              <p className="text-white font-semibold">{data.ourPricing}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Screenshot */}
      <section className="mb-16 sm:mb-24 max-w-6xl mx-auto px-4">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
          <img
            src="/product-frames/dashboard.webp"
            alt="Rooflyzer dashboard — roofing business management software"
            className="w-full h-auto"
            loading="lazy"
          />
        </div>
      </section>

      {/* Why We Win Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Why Roofing Contractors Choose Rooflyzer
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {data.whyWeWin.map((item, idx) => {
              const IconComponent = iconMap[item.icon] || Star;
              return (
                <div
                  key={idx}
                  className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-slate-700 transition-colors"
                >
                  <div className="w-12 h-12 bg-slate-800 border border-slate-700/80 rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="size-6 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-white/70 text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-16 px-4 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Feature-by-Feature Comparison
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Feature</th>
                  <th className="text-center py-3 px-4 text-white font-semibold text-sm w-28">
                    Rooflyzer
                  </th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium text-sm w-28">
                    {data.competitorName}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.featureComparison.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white/[0.02]' : ''}>
                    <td className="py-3 px-4 text-white text-sm">{row.feature}</td>
                    <td className="py-3 px-4 text-center">
                      <StatusBadge value={row.rooflyzer} />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <StatusBadge value={row.competitor} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {data.faqs.map((item, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">{item.question}</h3>
                <p className="text-white/70">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Switch from {data.competitorName}?
          </h2>
          <p className="text-lg text-white/70 mb-8">
            Join roofing contractors who chose Rooflyzer for better features at a lower price.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://auth.rooflyzer.pro/signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-950 font-semibold rounded-lg hover:bg-slate-100 shadow-sm transition-colors"
            >
              Start Your Free Trial
            </a>
            <Link
              to="/demo"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors"
            >
              Request a Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
